/*
 * fabric-quantity.js
 * Sell-by-the-meter support for fabric products.
 *
 * Model: the smallest sellable slice (default 0.25 m) is one Shopify "unit".
 *   meters  = units * step
 *   units   = round(meters / step)
 * The storefront always shows METERS; the cart/checkout store integer UNITS.
 *
 * This file is inert unless a <fabric-quantity> element is on the page, so it
 * never affects non-fabric products.
 */
(function () {
  'use strict';

  /* ---- money formatting (mirrors Shopify's money filter, no jQuery) ---- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{amount}}';

    function defaultTo(num, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var n = (cents / 100.0).toFixed(precision);
      var parts = n.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    switch ((fmt.match(placeholderRegex) || [])[1]) {
      case 'amount':
        value = defaultTo(2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(2, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = defaultTo(2, "'", '.');
        break;
      default:
        value = defaultTo(2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  /* ---- pretty-print meters: 0.5, 0.75, 1, 1.25 (no trailing zeros) ---- */
  function fmtMeters(m) {
    return parseFloat(m.toFixed(3)).toString();
  }

  /* ---- snap a meter value to the step grid, clamp to [min, max] ---- */
  function snap(m, step, min, max) {
    var stepsFromMin = Math.round((m - min) / step);
    var v = min + stepsFromMin * step;
    v = Math.round(v / step) * step;
    if (v < min) v = min;
    if (max != null && !isNaN(max) && v > max) {
      // largest grid value <= max
      v = min + Math.floor((max - min) / step + 1e-9) * step;
    }
    return parseFloat(v.toFixed(3));
  }

  class FabricQuantity extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.step = parseFloat(this.dataset.step) || 0.25;
      this.min = parseFloat(this.dataset.min) || this.step;
      this.max = this.dataset.max === '' ? null : parseFloat(this.dataset.max);
      this.unitsPerMeter = parseInt(this.dataset.unitsPerMeter, 10) || Math.round(1 / this.step);
      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.unitLabel = this.dataset.unitLabel || 'm';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';

      this.display = this.querySelector('[data-fq="display"]');
      this.unitsInput = this.querySelector('[data-fq="units"]');
      this.lengthProp = this.querySelector('[data-fq="length-prop"]');
      this.totalEl = this.querySelector('[data-fq="total"]');
      this.noteEl = this.querySelector('[data-fq="note"]');
      this.minusBtn = this.querySelector('[data-fq="minus"]');
      this.plusBtn = this.querySelector('[data-fq="plus"]');

      var start = parseFloat(this.display && this.display.value);
      if (isNaN(start)) start = this.min;
      this.value = snap(start, this.step, this.min, this.max);

      if (this.minusBtn) this.minusBtn.addEventListener('click', () => this.change(-this.step));
      if (this.plusBtn) this.plusBtn.addEventListener('click', () => this.change(this.step));
      if (this.display) {
        this.display.addEventListener('change', () => this.applyTyped());
        this.display.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); this.applyTyped(); this.display.blur(); }
        });
      }
      this.render();
    }

    change(delta) {
      this.value = snap(this.value + delta, this.step, this.min, this.max);
      this.setNote('');
      this.render();
    }

    // Handle a typed length: snap to the step grid, clamp to [min, stock], explain any adjustment.
    applyTyped() {
      var requested = parseFloat(this.display.value);
      if (isNaN(requested)) {
        this.value = this.min;
        this.setNote('Enter a length in ' + this.unitLabel);
        this.render();
        return;
      }
      var snapped = snap(requested, this.step, this.min, this.max);
      this.value = snapped;
      if (requested < this.min - 1e-9) {
        this.setNote('Minimum ' + fmtMeters(this.min) + ' ' + this.unitLabel);
      } else if (this.max != null && !isNaN(this.max) && requested > this.max + 1e-9) {
        this.setNote('Only ' + fmtMeters(this.max) + ' ' + this.unitLabel + ' available');
      } else if (Math.abs(snapped - requested) > 1e-9) {
        this.setNote('Rounded to ' + fmtMeters(snapped) + ' ' + this.unitLabel + ' (sold in ' + fmtMeters(this.step) + ' ' + this.unitLabel + ' steps)');
      } else {
        this.setNote('');
      }
      this.render();
    }

    setNote(msg) {
      if (this.noteEl) this.noteEl.textContent = msg || '';
    }

    render() {
      var units = Math.round(this.value * this.unitsPerMeter);
      if (this.display) this.display.value = fmtMeters(this.value);
      if (this.unitsInput) this.unitsInput.value = units;
      if (this.lengthProp) this.lengthProp.value = fmtMeters(this.value) + ' ' + this.unitLabel;
      if (this.totalEl && this.unitPrice) {
        this.totalEl.textContent = 'Total: ' + formatMoney(this.unitPrice * units, this.moneyFormat);
      }
      var atMin = this.value <= this.min + 1e-9;
      var atMax = this.max != null && !isNaN(this.max) && this.value >= this.max - 1e-9;
      if (this.minusBtn) this.minusBtn.disabled = atMin;
      if (this.plusBtn) this.plusBtn.disabled = atMax;
    }
  }

  if (!customElements.get('fabric-quantity')) {
    customElements.define('fabric-quantity', FabricQuantity);
  }

  /* ---- Cart: relabel a fabric line's unit count as meters -----------------
   * Cart rows render a native units stepper. For fabric rows (marked with
   * data-fabric-unit-m) we show the meters readout next to it. Re-runs after
   * the theme re-renders the cart (cartUpdate pub/sub or DOM mutations).
   */
  function relabelCart(root) {
    (root || document).querySelectorAll('[data-fabric-unit-m]').forEach(function (el) {
      var unitM = parseFloat(el.dataset.fabricUnitM) || 0.25;
      var input = el.querySelector('input[name="updates[]"], .quantity__input');
      if (!input) return;
      var units = parseInt(input.value, 10);
      if (isNaN(units)) return;
      var meters = parseFloat((units * unitM).toFixed(3));
      var out = el.querySelector('[data-fabric-meters-out]');
      if (out) out.textContent = meters + ' ' + (el.dataset.fabricUnitLabel || 'm');
    });
  }

  document.addEventListener('DOMContentLoaded', function () { relabelCart(); });
  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(function () { relabelCart(); }, 60);
    });
  }
  // Fallback: observe cart containers for re-render.
  var cartRoot = document.querySelector('cart-items, cart-drawer-items, cart-drawer');
  if (cartRoot && 'MutationObserver' in window) {
    new MutationObserver(function () { relabelCart(); }).observe(cartRoot, { childList: true, subtree: true });
  }
})();
